import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { date, formatTime, formatTimeDistance } from '../time.js';
import isPending from '../helpers/is-pending.js';
import Password from './password.gjs';
import PageHeader from './page-header.gjs';
import PageContent from './page-content.gjs';
import PageInfo from './page-info.gjs';
import LoadingIndicator from './loading-indicator.gjs';
import DateTimeElapsed from './date-time-elapsed.gjs';
import { faUser } from '../icons/solid.js';

export default class AccountComponent extends Component {
  @service session;
  @service time;
  @service notify;
  @service intl;
  @service requestManager;

  @tracked basicCredentials;
  @tracked basicCredentialsPromise;

  @tracked refreshAccessTokenPromise;

  constructor() {
    super(...arguments);
    if (
      this.session.data.authenticated.authenticator ===
      'authenticator:oidc-auth-code'
    ) {
      this.fetchBasicCredentials();
    }
  }

  @action fetchBasicCredentials() {
    this.basicCredentialsPromise = this.#fetchBasicCredentials();
  }

  @action async #fetchBasicCredentials() {
    try {
      const response = await this.requestManager.request({
        url: '/api/credentials',
      });

      this.basicCredentials = response.content;
    } catch (error) {
      if (error.status === 404) {
        this.basicCredentials = null;
        return;
      }

      this.notify.alert(
        this.intl.t('account.basic-authentication.error-getting-credentials'),
        error,
      );

      throw error;
    }
  }

  @action async createBasicCredentials() {
    this.basicCredentialsPromise = this.#createBasicCredentials();
  }

  @action async #createBasicCredentials() {
    try {
      const response = await this.requestManager.request({
        url: '/api/credentials',
        method: 'POST',
        headers: new Headers([['Content-Type', 'application/json']]),
        body: JSON.stringify({
          refreshToken: this.session.data.authenticated.refresh_token,
        }),
      });

      this.basicCredentials = response.content;
    } catch (error) {
      if (error.status === 409) {
        this.notify.alert(
          this.intl.t('account.basic-authentication.credentials-exist-message'),
          error,
        );
        return;
      }

      this.notify.alert(
        this.intl.t('account.basic-authentication.error-creating-credentials'),
        error,
      );

      throw error;
    }
  }

  @action async deleteBasicCredentials() {
    this.basicCredentialsPromise = this.#deleteBasicCredentials();
  }

  @action async #deleteBasicCredentials() {
    try {
      await this.requestManager.request({
        url: 'api/credentials',
        method: 'DELETE',
      });
      this.basicCredentials = null;
    } catch (error) {
      this.notify.alert(
        this.intl.t('account.basic-authentication.error-deleting-credentials'),
        error,
      );
      throw error;
    }
  }

  @action async refreshAccessToken() {
    this.refreshAccessTokenPromise = this.session.refreshAccessToken();
  }

  <template>
    <PageHeader
      @icon={{faUser}}
      @title={{t "account.title"}}
      @subtitle={{this.session.data.authenticated.user.name}}
      @description={{t "account.subtitle"}}
      @onClose={{@onClose}}
    />

    <PageInfo>
      {{t "account.description"}}
    </PageInfo>

    <PageContent>
      <div class="px-2">
        <h2 class="text-xl font-bold py-2">{{t "account.roles.title"}}</h2>
        <ul class="py-2">
          {{#each this.session.data.authenticated.user.roles as |role|}}
            <li>{{role}}</li>
          {{else}}
            <li class="italic">{{t "account.roles.no-roles-message"}}</li>
          {{/each}}
        </ul>

        {{#if
          (eq
            this.session.data.authenticated.authenticator
            "authenticator:oidc-auth-code"
          )
        }}
          <h2 class="text-xl font-bold py-2 flex flex-row">
            <span>{{t "account.basic-authentication.title"}}</span>
            <LoadingIndicator @promise={{this.basicCredentialsPromise}} />
          </h2>

          {{#if this.basicCredentials}}

            <div class="overflow-x-auto my-2">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>{{t "login.username"}}</th>
                    <th>{{t "login.password"}}</th>
                    <th>{{t "created"}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>{{this.basicCredentials.username}}</code></td>
                    <td>
                      <Password @value={{this.basicCredentials.password}} />
                    </td>
                    <td>
                      <DateTimeElapsed
                        @value={{this.basicCredentials.created}}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="py-2">
              <button
                type="button"
                class="btn btn-ghost text-primary uppercase"
                {{on "click" this.createBasicCredentials}}
                disabled={{isPending this.basicCredentialsPromise}}
              >
                {{t "reset"}}
              </button>
              <button
                type="button"
                class="btn btn-ghost text-primary uppercase"
                {{on "click" this.deleteBasicCredentials}}
                disabled={{isPending this.basicCredentialsPromise}}
              >
                {{t "delete"}}
              </button>
            </div>

          {{else}}

            <p class="py-2">
              {{t "account.basic-authentication.no-credentials-message"}}
            </p>

            <div class="py-2">
              <button
                type="button"
                class="btn btn-ghost text-primary uppercase"
                {{on "click" this.createBasicCredentials}}
                disabled={{isPending this.basicCredentialsPromise}}
              >
                {{t "create"}}
              </button>
            </div>

          {{/if}}

        {{/if}}

        <h2 class="text-xl font-bold py-2 flex flex-row">
          <div>{{t "account.token"}}</div>
          <LoadingIndicator @promise={{this.refreshAccessTokenPromise}} />
        </h2>
        <p class="break-words py-2">
          {{this.session.data.authenticated.access_token}}
        </p>

        <p class="py-2 italic">
          {{t "expires"}}
          {{formatTime (date this.session.data.authenticated.expires_at)}}
          ({{formatTimeDistance
            (date this.session.data.authenticated.expires_at)
            this.time.now
          }})
        </p>

        <div class="py-2">
          <button
            type="button"
            class="btn btn-ghost text-primary uppercase"
            {{on "click" this.refreshAccessToken}}
            disabled={{isPending this.refreshAccessTokenPromise}}
          >
            {{t "refresh"}}
          </button>
        </div>
      </div>
    </PageContent>
  </template>
}
